import { PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { Button, Flex, IconButton, TextArea, TextField } from '@radix-ui/themes'
import { useMemo, useState } from 'react'
import { RewardTypeSummary, RewardScheduleWithGame } from '@Generated/back-office-client/src'
import { EditDrawerFrame } from './edit-drawer-frame'
import { PrizeFormData, PrizeScheduleForm } from './form'
import { Field, FieldError } from './form-fields'
import { useSaveScheduleContent } from './use-save-schedule-content'

type PrizeEditDrawerProps = {
  schedule: RewardScheduleWithGame
  reward: RewardTypeSummary
  onClose: () => void
}

export const PrizeEditDrawer = ({ schedule, reward, onClose }: PrizeEditDrawerProps) => {
  const [prize, setPrize] = useState<PrizeFormData>(() => PrizeScheduleForm.toPrizeForm(reward))
  const errors = useMemo(
    () => PrizeScheduleForm.validatePrize(prize, schedule.game),
    [prize, schedule.game],
  )
  const { save, saveError, isSaving } = useSaveScheduleContent(schedule.id, onClose)

  const updateMetadata = (rowIndex: number, field: 'key' | 'value', value: string) =>
    setPrize({
      ...prize,
      metadata: prize.metadata.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row)),
    })

  return (
    <EditDrawerFrame
      title={`Edit ${reward.rewardName}`}
      saveError={saveError}
      isSaving={isSaving}
      canSave={Object.keys(errors).length === 0}
      onSave={() => save(PrizeScheduleForm.toPrizeUpdate(prize))}
      onClose={onClose}>
      <Field
        label="Prize name"
        hint={`Shown on the wheel; up to ${schedule.game.wheelTextLimit} characters.`}>
        <TextField.Root
          aria-label="Prize name"
          value={prize.rewardName}
          onChange={(e) => setPrize({ ...prize, rewardName: e.target.value })}
        />
        <FieldError errors={errors} path="rewardName" />
      </Field>
      <Field label="Post-win header">
        <TextField.Root
          aria-label="Post-win header"
          value={prize.header}
          onChange={(e) => setPrize({ ...prize, header: e.target.value })}
        />
        <FieldError errors={errors} path="header" />
      </Field>
      <Field label="Post-win description">
        <TextArea
          aria-label="Post-win description"
          value={prize.description}
          onChange={(e) => setPrize({ ...prize, description: e.target.value })}
        />
        <FieldError errors={errors} path="description" />
      </Field>
      <Field
        label="Disclaimer"
        hint="Fine print shown beneath the description, such as offer terms. Leave blank for none.">
        <TextArea
          aria-label="Disclaimer"
          value={prize.disclaimer}
          onChange={(e) => setPrize({ ...prize, disclaimer: e.target.value })}
        />
      </Field>
      <Field label="Button label" hint="Leave blank to hide the button.">
        <TextField.Root
          aria-label="Button label"
          value={prize.callToAction}
          onChange={(e) => setPrize({ ...prize, callToAction: e.target.value })}
        />
      </Field>
      <Field
        label="CTA metadata"
        hint="Sent to the host app, unchanged, when the button is pressed.">
        <Flex direction="column" gap="2">
          {prize.metadata.map((row, rowIndex) => (
            <Flex key={rowIndex} direction="column" gap="1">
              <Flex gap="2" align="center">
                <TextField.Root
                  aria-label="Metadata key"
                  placeholder="key"
                  value={row.key}
                  onChange={(e) => updateMetadata(rowIndex, 'key', e.target.value)}
                />
                <TextField.Root
                  aria-label="Metadata value"
                  placeholder="value"
                  value={row.value}
                  onChange={(e) => updateMetadata(rowIndex, 'value', e.target.value)}
                />
                <IconButton
                  variant="ghost"
                  color="red"
                  aria-label="Remove metadata"
                  onClick={() =>
                    setPrize({
                      ...prize,
                      metadata: prize.metadata.filter((_, i) => i !== rowIndex),
                    })
                  }>
                  <TrashIcon />
                </IconButton>
              </Flex>
              <FieldError errors={errors} path={`metadata.${rowIndex}.key`} />
            </Flex>
          ))}
        </Flex>
        <Button
          variant="soft"
          size="1"
          style={{ alignSelf: 'flex-start' }}
          onClick={() =>
            setPrize({ ...prize, metadata: [...prize.metadata, { key: '', value: '' }] })
          }>
          <PlusIcon /> Add metadata
        </Button>
      </Field>
    </EditDrawerFrame>
  )
}
